import { alpha, Box, Radio, Stack, Typography } from '@mui/material';
import React from 'react';

import { useTranslation } from 'react-i18next';

import { Popover } from '@/components/utils/Popover/Popover';

import { CardContainer, CardDisplay } from '@/components/displays/CardDisplay';
import { CharacterCardsGameType } from '@/types/slide';

import QuestionMarkIcon from '@mui/icons-material/QuestionMark';

type QuestionSettingsProps = {
    cardType: number;
    gameType: CharacterCardsGameType;
};

interface Props {
    anchorEl: HTMLElement | null;
    value: QuestionSettingsProps;
    onClose: () => void;
    onUpdate: (settings: QuestionSettingsProps) => void;
    slotProps?: { paper?: { sx?: object; ref: React.Ref<HTMLDivElement> } };
}

const SampleCardDisplay = (props: { cardType: number; enabled: boolean }) => {
    if (props.cardType == 1) {
        if (props.enabled) {
            return (
                <CardDisplay sx={(theme) => ({ borderRadius: 0.5, width: 40, height: 60, boxShadow: 0, backgroundColor: theme.palette.grey[300] })} direction={'row'}>
                    <QuestionMarkIcon sx={(theme) => ({ width: 30, height: 30, color: theme.palette.grey[600] })} />
                </CardDisplay>
            );
        } else {
            return <CardDisplay sx={(theme) => ({ borderRadius: 0.5, width: 40, height: 60, boxShadow: 0, backgroundColor: theme.palette.grey[400] })} direction={'row'} />;
        }
    } else if (props.cardType == 2) {
        if (props.enabled) {
            return (
                <CardDisplay sx={(theme) => ({ borderRadius: 0.5, width: 60, height: 40, boxShadow: 0, backgroundColor: theme.palette.grey[300] })} direction={'column'}>
                    <QuestionMarkIcon sx={(theme) => ({ width: 30, height: 30, color: theme.palette.grey[600] })} />
                </CardDisplay>
            );
        } else {
            return <CardDisplay sx={(theme) => ({ borderRadius: 0.5, width: 60, height: 40, boxShadow: 0, backgroundColor: theme.palette.grey[400] })} direction={'column'} />;
        }
    } else if (props.cardType == 3) {
        if (props.enabled) {
            return (
                <>
                    <CardDisplay
                        sx={(theme) => ({
                            position: 'absolute',
                            transform: 'rotate(-5deg) translate(-20%,0)',
                            borderRadius: 0.5,
                            width: 40,
                            height: 60,
                            boxShadow: 0,
                            backgroundColor: theme.palette.grey[400],
                        })}
                        direction={'row'}
                    />
                    <CardDisplay sx={(theme) => ({ borderRadius: 0.5, width: 40, height: 60, boxShadow: 0, backgroundColor: theme.palette.grey[300] })} direction={'row'}>
                        <QuestionMarkIcon sx={(theme) => ({ width: 30, height: 30, color: theme.palette.grey[600] })} />
                    </CardDisplay>
                </>
            );
        } else {
            return (
                <CardDisplay
                    sx={(theme) => ({
                        borderRadius: 0.5,
                        width: 40,
                        height: 60,
                        outline: '2px solid',
                        outlineColor: theme.palette.grey[400],
                        outlineOffset: 4,
                        boxShadow: 0,
                        backgroundColor: theme.palette.grey[300],
                    })}
                    direction={'row'}
                >
                    <QuestionMarkIcon sx={(theme) => ({ width: 30, height: 30, color: theme.palette.grey[600] })} />
                </CardDisplay>
            );
        }
    }
    return <></>;
};

export const QuestionSettingsPopover: React.FC<Props> = ({ anchorEl, onClose, onUpdate, value, slotProps }) => {
    const { t } = useTranslation();

    const open = Boolean(anchorEl);

    return (
        <Popover
            title={t('slide.components.characterCards.questionSettings.title')}
            open={open}
            anchorEl={anchorEl}
            onClose={() => onClose()}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: -8,
                horizontal: 'center',
            }}
            slotProps={{
                paper: {
                    ref: slotProps?.paper?.ref,
                    sx: {
                        width: '400px',
                        display: 'flex',
                        p: 2,
                    },
                },
            }}
        >
            <Stack spacing={1}>
                {value.cardType == 1 && (
                    <Typography variant='body1' sx={{ ml: 1 }}>
                        {t('slide.components.characterCards.questionSettings.descriptionLeftRight')}
                    </Typography>
                )}
                {value.cardType == 2 && (
                    <Typography variant='body1' sx={{ ml: 1 }}>
                        {t('slide.components.characterCards.questionSettings.descriptionTopBottom')}
                    </Typography>
                )}
                {value.cardType == 3 && (
                    <Typography variant='body1' sx={{ ml: 1 }}>
                        {t('slide.components.characterCards.questionSettings.descriptionInOut')}
                    </Typography>
                )}
                <Box
                    sx={(theme) => ({
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        px: 1,
                        width: '100%',
                        height: 120,
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                    })}
                >
                    {value.cardType == 1 && (
                        <Box>
                            <Stack direction={'row'} sx={{ height: '100%', gap: '8px' }}>
                                <CardContainer>
                                    <SampleCardDisplay cardType={1} enabled={value.gameType == 'First' || value.gameType == 'Both'} />
                                </CardContainer>
                                <CardContainer>
                                    <SampleCardDisplay cardType={1} enabled={value.gameType == 'Second' || value.gameType == 'Both'} />
                                </CardContainer>
                            </Stack>
                        </Box>
                    )}

                    {value.cardType == 2 && (
                        <Box>
                            <Stack direction={'column'} sx={{ height: '100%', gap: '8px' }}>
                                <CardContainer>
                                    <SampleCardDisplay cardType={2} enabled={value.gameType == 'First' || value.gameType == 'Both'} />
                                </CardContainer>
                                <CardContainer>
                                    <SampleCardDisplay cardType={2} enabled={value.gameType == 'Second' || value.gameType == 'Both'} />
                                </CardContainer>
                            </Stack>
                        </Box>
                    )}

                    {value.cardType == 3 && (
                        <Box>
                            <Stack direction={'row'} sx={{ height: '100%', gap: '8px' }}>
                                <CardContainer>
                                    <SampleCardDisplay cardType={3} enabled={value.gameType == 'Both'} />
                                </CardContainer>
                            </Stack>
                        </Box>
                    )}
                </Box>
                <Box>
                    <Stack
                        direction={'row'}
                        sx={{
                            justifyContent: 'space-around',
                            alignItems: 'center',
                        }}
                    >
                        {value.cardType == 3 ? (
                            <>
                                <Box>
                                    <Stack direction='row' spacing={1} alignItems='center'>
                                        <Radio
                                            checked={value.gameType == 'Second'}
                                            onChange={() => {
                                                onUpdate({
                                                    ...value,
                                                    gameType: 'Second',
                                                });
                                            }}
                                        />

                                        <Typography>{t('slide.components.characterCards.questionSettings.inside')}</Typography>
                                    </Stack>
                                </Box>
                                <Box>
                                    <Stack direction='row' spacing={1} alignItems='center'>
                                        <Radio
                                            checked={value.gameType == 'Both'}
                                            onChange={() => {
                                                onUpdate({
                                                    ...value,
                                                    gameType: 'Both',
                                                });
                                            }}
                                        />
                                        <Typography>{t('slide.components.characterCards.questionSettings.both')}</Typography>
                                    </Stack>
                                </Box>
                            </>
                        ) : (
                            <>
                                <Box>
                                    <Stack direction='row' spacing={1} alignItems='center'>
                                        <Radio
                                            checked={value.gameType == 'First'}
                                            onChange={() => {
                                                onUpdate({
                                                    ...value,
                                                    gameType: 'First',
                                                });
                                            }}
                                        />
                                        {value.cardType == 1 && <Typography>{t('slide.components.characterCards.questionSettings.left')}</Typography>}
                                        {value.cardType == 2 && <Typography>{t('slide.components.characterCards.questionSettings.top')}</Typography>}
                                    </Stack>
                                </Box>
                                <Box>
                                    <Stack direction='row' spacing={1} alignItems='center'>
                                        <Radio
                                            checked={value.gameType == 'Second'}
                                            onChange={() => {
                                                onUpdate({
                                                    ...value,
                                                    gameType: 'Second',
                                                });
                                            }}
                                        />
                                        {value.cardType == 1 && <Typography>{t('slide.components.characterCards.questionSettings.right')}</Typography>}
                                        {value.cardType == 2 && <Typography>{t('slide.components.characterCards.questionSettings.bottom')}</Typography>}
                                    </Stack>
                                </Box>
                                <Box>
                                    <Stack direction='row' spacing={1} alignItems='center'>
                                        <Radio
                                            checked={value.gameType == 'Both'}
                                            onChange={() => {
                                                onUpdate({
                                                    ...value,
                                                    gameType: 'Both',
                                                });
                                            }}
                                        />
                                        <Typography>{t('slide.components.characterCards.questionSettings.both')}</Typography>
                                    </Stack>
                                </Box>
                            </>
                        )}
                    </Stack>
                </Box>
            </Stack>
        </Popover>
    );
};
